import { bunja } from 'bunja';
import { v4 as uuid } from 'uuid';

import { OrderTypeEnum } from '@flipster/apis/types';
import { postSpotMarketOrderApi, postSpotOrderApi } from '@flipster/apis/v2/trade';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { i18nBunja } from '@flipster/i18n';
import { refDetailTimes } from '@flipster/r230522/original/server-time';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { positionTabTypeAtom } from '../../../etc-section/spot/your-holdings';
import { tradeSymbolBunja } from '../../../symbol';
import { OrderEventContext } from '../../tracker';
import { spotFormBunja } from './form';

export const spotActionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const form = bunja.use(spotFormBunja);
  const { symbol, symbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { getT } = bunja.use(i18nBunja);

  const t = getT();
  const transTrade = getT('Trade.r250401');

  const submitOrder = async (ctx: OrderEventContext) => {
    const attribution = store.get(orderAttributionAtom);
    const symbolInfo = store.get(symbolInfoAtom);
    const side = store.get(form.sideAtom);
    const orderType = store.get(form.orderStateAtom).type;
    const priceType = store.get(form.priceTypeAtom);
    const amount = store.get(form.amountAtom).toString();
    const quantity = store.get(form.quantityAtom).toString();
    const referencePrice = store.get(form.referencePriceAtom);
    const triggerPrice = store.get(form.triggerPriceAtom) || '0';
    const limitPrice = store.get(form.limitPriceAtom) || '0';
    const isBbo = store.get(form.isBboAtom);
    const bboPrice = store.get(form.bboPriceAtom);

    try {
      const requestId = uuid();
      const timestamp = dateToEpochString(new Date());
      const orderTypeValue = orderType === 'amount' ? { amount } : { qty: quantity };
      const refTimestamp = refDetailTimes[symbol];
      const price = bboPrice;

      switch (priceType) {
        case 'market': {
          if (price === '0') throw new Error('ZeroPrice');
          ctx.mark('apiStartTime');
          // In private/wallet message, cannot heuristically find response message
          ctx.socketResponseSupported = false;
          const { data } = await postSpotMarketOrderApi(symbol, {
            requestId,
            timestamp,
            side,
            price,
            ...orderTypeValue,
            ...refTimestamp,
            attribution,
            orderType: OrderTypeEnum['Market'],
          });
          ctx.markRequestId(data.asset.symbol);
          ctx.mark('apiEndTime');
          store.set(positionTabTypeAtom, 'purchased');

          const isIoc = data.order.remainingQty && Num(data.order.remainingQty).isGreaterThan(0);
          if (isIoc) {
            const successAmount = BigNumber.max(
              Num(data.order?.qty || '0')
                .abs()
                .minus(Num(data.order?.remainingQty || '0').abs()),
              0,
            );
            const remainingAmount = Num(data.order?.remainingQty || '0').abs();

            snackbar.error({
              message: t('Trade.r260224.Market_order_partial_fill_toast', {
                amountSymbolFilled: `${numberFormatter(successAmount, {
                  precision: symbolInfo?.tradeQtyPrecision,
                  toFixed: true,
                })} ${symbolInfo?.positionUnit}`,
                value: numberFormatter(data.asset.avgPrice, {
                  precision: symbolInfo?.pricePrecision,
                  toFixed: true,
                }),
                amountSymbolRemaining: `${numberFormatter(remainingAmount, {
                  precision: symbolInfo?.tradeQtyPrecision,
                  toFixed: true,
                })} ${symbolInfo?.positionUnit}`,
              }),
            });
          } else {
            snackbar.success({ message: transTrade('Order_executed_toast', { symbol: symbolInfo?.displayShortName }) });
          }
          break;
        }
        case 'trigger': {
          if (triggerPrice === '0') throw new Error('ZeroTriggerPrice');
          ctx.mark('apiStartTime');
          const { data } = await postSpotOrderApi(symbol, {
            side,
            requestId,
            timestamp,
            ...refTimestamp,
            ...orderTypeValue,
            orders: [
              {
                triggerPrice,
                triggerDirection: Num(triggerPrice).isGreaterThanOrEqualTo(referencePrice) ? 'Up' : 'Down',
              },
            ],
            attribution,
          });
          const order = data.orders.find((order) => 'orderId' in order);
          if (order) {
            ctx.markRequestId(order.orderId);
          } else {
            ctx.socketResponseSupported = false;
          }
          ctx.mark('apiEndTime');
          snackbar.success({ message: transTrade('Order_submitted_toast', { symbol: symbolInfo?.displayShortName }) });
          break;
        }
        case 'limit': {
          if (limitPrice === '0') throw new Error('ZeroPrice');
          ctx.mark('apiStartTime');
          // In private/wallet message, cannot heuristically find response message
          ctx.socketResponseSupported = false;
          const { data } = await postSpotMarketOrderApi(symbol, {
            requestId,
            timestamp,
            side,
            ...orderTypeValue,
            ...refTimestamp,
            attribution,
            price: isBbo ? undefined : limitPrice,
            priceMatchStrategy: isBbo ? 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1' : undefined,
            orderType: OrderTypeEnum['Limit'],
          });
          ctx.markRequestId(data.asset.symbol);
          ctx.mark('apiEndTime');
          snackbar.success({ message: transTrade('Order_submitted_toast', { symbol: symbolInfo?.displayShortName }) });
          break;
        }
        default:
          break;
      }
    } catch (err) {
      ctx.mark('apiEndTime');
      const isClientError = !checkAxiosError(err) && err instanceof Error;
      const type = isClientError ? 'unknown' : 'api';
      const error = isClientError ? err.message : getErrorMessage(err);
      snackbar.error({ message: error });
      ctx.markError({ type, error });
    } finally {
      ctx.mark('postApiEndTime');
      ctx.markApiFinish();
    }
  };

  return {
    submitOrder,
  };
});
spotActionBunja.debugLabel = 'spotActionBunja';
